import styles from "./style.postcss";

import React from "react";
import { bindActionCreators } from "redux";
import { withRouter } from "react-router";
import { push } from "react-router-redux";
import { Map, List } from "immutable";
import is from "is_js";
import ReactGA from "react-ga4";
import PropTypes from "prop-types";
import classnames from "classnames";

import {
  connect,
  ApiCall,
  ApiResponseHelper,
  mediaQuery,
} from "omni-common-ui";

import { ConfirmationPrompt } from "domain/ConfirmationPrompt";
import Detailsboard from "components/Detailsboard";
import UnsavedChangesWarning from "components/UnsavedChangesWarning";
import UnsavedChangesTrackingPageComponent from "components/UnsavedChangesTrackingPageComponent";
import InfoDialog from "components/InfoDialog";
import { actions as GroupMembers } from "data/GroupMembers";
import { ProgressTest } from "data/ProgressTests";
import * as speakingSubSkillOrderHFv4 from "data/ProgressTests/speakingSubSkillOrderHFv4";
import {
  checkIsUnitQuiz,
  getProgressTestPlanFromIndex,
} from "data/ProgressTestsPlan/accessors";

import RubricSkillTable from "./RubricSkillTable";

const MARKING = "edit";

const toMap = (arr, key) =>
  arr.reduce((obj, item) => {
    // eslint-disable-next-line no-param-reassign
    obj[item[key]] = item;
    return obj;
  }, {});

export class ProgressTestRubricView extends UnsavedChangesTrackingPageComponent {
  constructor(props) {
    super(props);
    this._queryListener = this._queryListener.bind(this);
    this._queryIsLg = mediaQuery("(min-width: 980px)");
    this.state = {
      lg: this._queryIsLg.matches,
      targetLanguagePopup: {
        isOpen: false,
        skillId: "",
      },
    };
    this.scrollers = [];
    Object.assign(this.state, {
      expandedInstructionTextsSubSkillIds: new Map(),
      subSkillsToSkillIds: new Map(),
    });

    this._renderSkills = this._renderSkills.bind(this);
  }

  componentWillMount() {
    this._buildSubSkillsToAbilityIdsMap(this.props);
    super.componentWillMount();
  }

  componentDidMount() {
    this._queryIsLg.addListener(this._queryListener);
  }

  componentWillReceiveProps(nextProps) {
    const { params, progressTest } = this.props;
    const { params: nextParams, progressTest: nextProgressTest } = nextProps;
    if (
      nextParams.studentId !== params.studentId ||
      (!progressTest && nextProgressTest)
    ) {
      this._buildSubSkillsToAbilityIdsMap(nextProps);
    }
  }

  componentWillUnmount() {
    this._queryIsLg.removeListener(this._queryListener);
  }

  routerWillLeave(nextLocation) {
    const that = this;
    const { subSkillsToAbilityIds } = this.state;

    const returnToView = (mode = "") =>
      that._forcePushRoute(that.props.buildRoute({ mode }));

    if (nextLocation && nextLocation.pathname.endsWith("/save")) {
      if (this._checkCanSave()) {
        setTimeout(
          () =>
            that._handleSave(subSkillsToAbilityIds),
          0,
        );
        returnToView();
      } else {
        returnToView("edit");
      }
    } else if (nextLocation && nextLocation.pathname.endsWith("/discard")) {
      this._buildSubSkillsToAbilityIdsMap(this.props);
      returnToView();
    } else {
      return super.routerWillLeave(nextLocation);
    }
  }

  _queryListener(mql) {
    this.setState({ lg: mql.matches });
  }

  _onScrollReady(scroll) {
    this.scrollers.push(scroll);
  }

  _isChangesPresent() {
    return (
      ProgressTestRubricView.isInMarkingRoute(this.props) &&
      this.state.originalSubSkillsToSkillIds &&
      this.state.originalSubSkillsToSkillIds !== this.state.subSkillsToSkillIds
    );
  }

  _buildSubSkillsToAbilityIdsMap(props) {
    const { rubricSkills, currentMember, progressTest } = props;
    if (is.undefined(currentMember)) {
      return;
    }
    const {
      studentGeneralInfo: { id: studentId },
    } = currentMember;

    if (!progressTest) return;

    const subSkillsToSkillIds = rubricSkills.reduce((map, rubricSkill) => {
      const subSkillsToAbilityIds = {};
      const studentSubSkillResults =
        (
          progressTest.studentProgressTestResults
            .get(studentId)
            .skillResults.find((skr) => skr.skillId === rubricSkill.skillId) ||
          {}
        ).subSkillResults || [];
      const subSkillResultsMap = toMap(studentSubSkillResults, "subSkillId");
      rubricSkill.subSkills.forEach((subSkill) => {
        const { subSkillId } = subSkill;
        subSkillsToAbilityIds[subSkillId] =
          subSkillResultsMap[subSkillId] &&
          subSkillResultsMap[subSkillId].currentResultAbilityId;
      });
      return map.set(`${rubricSkill.skillId}`, new Map(subSkillsToAbilityIds));
    }, new Map());

    this.setState({
      subSkillsToSkillIds,
      originalSubSkillsToSkillIds: subSkillsToSkillIds,
    });
  }

  _checkCanSave(subSkillsToSkillIds = this.state.subSkillsToSkillIds) {
    const { rubricSkills } = this.props;

    const isCompelte = rubricSkills.every((rubricSkill) => {
      const numberOfSelectedAbilityPoints = subSkillsToSkillIds
        .get(rubricSkill.skillId)
        .map((v) => v).size;
      return numberOfSelectedAbilityPoints === rubricSkill.subSkills.size;
    });

    if (!isCompelte) {
      ConfirmationPrompt.showReminder(
        "Incomplete Skill",
        "Please select an ability point for each of the sub-skills before saving.",
      );
      return false;
    }
    return true;
  }

  _handleSave(subSkillsToSkillIds = this.state.subSkillsToSkillIds) {
    const {
      rubricSkills,
      currentMember,
      currentProgressTestId,
      submitProgressTestResults,
      progressReportId,
      params,
      isUnitQuiz,
    } = this.props;

    const progressTest = ProgressTest.serialize(
      ProgressTest.create({
        studentProgressTestResults: [
          {
            studentId: currentMember.studentGeneralInfo.id,
            skillResults: rubricSkills
              .map((rubricSkill) => ({
                skillId: rubricSkill.skillId,
                subSkillResults: rubricSkill.subSkills
                  .map(({ subSkillId }) => ({
                    subSkillId,
                    currentResultAbilityId:
                      subSkillsToSkillIds
                        .get(rubricSkill.skillId)
                        .get(subSkillId) || null,
                  }))
                  .toArray(),
              }))
              .toArray(),
          },
        ],
      }),
    );
    return isUnitQuiz
      ? submitProgressTestResults(
          params.groupId,
          currentProgressTestId,
          progressTest,
          progressReportId,
          true,
        )
      : submitProgressTestResults(
          params.groupId,
          currentProgressTestId,
          progressTest,
        );
  }

  _onInstructionTextExpandCollapseClicked(skillId, subSkillId) {
    const { expandedInstructionTextsSubSkillIds } = this.state;

    const action = expandedInstructionTextsSubSkillIds.getIn([
      skillId,
      subSkillId,
    ])
      ? "collapse"
      : "expand";
    ReactGA.event({
      category: "Progress Tests",
      action: `Clicked ${action} scoring criteria`,
      label: `Clicked ${action} scoring criteria for sub skill ${subSkillId} in skill ${skillId}`,
    });

    this.setState(
      {
        expandedInstructionTextsSubSkillIds:
          expandedInstructionTextsSubSkillIds.updateIn(
            [skillId, subSkillId],
            (currentToggleState) => !currentToggleState,
          ),
      },
      () => {
        this.scrollers.forEach((scroll) => {
          scroll.refresh();
        });
      },
    );
  }

  _onIndividualAbilityButtonClicked(subSkillId, resultAbilityId, skillId) {
    const { subSkillsToSkillIds } = this.state;

    ReactGA.event({
      category: "Progress Tests",
      action: "Clicked score button",
      label: `Clicked score button ${resultAbilityId} for skill ${skillId} and sub skill ${subSkillId}`,
    });
    this.setState({
      subSkillsToSkillIds: subSkillsToSkillIds.setIn(
        [skillId, subSkillId],
        resultAbilityId,
      ),
    });
  }

  _onCheckAllAbility(skillId, checked, abilityId) {
    const { subSkillsToSkillIds } = this.state;
    if (checked) {
      this.setState({
        subSkillsToSkillIds: subSkillsToSkillIds.update(
          skillId,
          (subSkillsToAbilityIds) => subSkillsToAbilityIds.map(() => abilityId),
        ),
      });
    }
  }

  _renderSkills(skills) {
    const { subSkillsToSkillIds, expandedInstructionTextsSubSkillIds } =
      this.state;
    const onIntructionToggleClick = (...args) =>
      this._onInstructionTextExpandCollapseClicked(...args);
    return skills.map((rubricSkill) => (
      <RubricSkillTable
        isMarking={ProgressTestRubricView.isInMarkingRoute(this.props)}
        isUnitQuiz={this.props.isUnitQuiz}
        rubricSkill={speakingSubSkillOrderHFv4.orderSpeakingSubSkillsIfNeeded(
          rubricSkill,
        )}
        showInstructionsFor={expandedInstructionTextsSubSkillIds}
        subSkillsToAbilityIds={subSkillsToSkillIds.get(
          `${rubricSkill.skillId}`,
        )}
        onCheckAllAbility={(...args) => this._onCheckAllAbility(...args)}
        onIntructionToggleClick={onIntructionToggleClick}
        onTargetLanguageButtonClick={(skillId) =>
          this._onTargetLanguageButtonClick(skillId)
        }
        onScoreClick={(...args) =>
          this._onIndividualAbilityButtonClicked(...args)
        }
      />
    ));
  }

  _renderView() {
    const { rubricSkills, progressTest } = this.props;

    if (!progressTest) return null;

    const multiColumns = !!(
      this.state.lg &&
      rubricSkills &&
      rubricSkills.size > 4
    );
    const masonryClass = classnames(styles.Masonry, {
      [styles.Masonry_multi]: multiColumns,
    });

    let listStart = [];
    let listEnd = [];
    if (multiColumns) {
      listStart = rubricSkills.filter((rubricSkill, index) => index % 2 === 0);
      listEnd = rubricSkills.filterNot((rubricSkill, index) => index % 2 === 0);
    }

    return (
      <div className={styles.ProgressTestRubricView}>
        <div className={masonryClass}>
          {multiColumns
            ? [
                <div className={styles.Masonry_flow}>
                  {this._renderSkills(listStart)}
                </div>,
                <div className={styles.Masonry_flow}>
                  {this._renderSkills(listEnd)}
                </div>,
              ]
            : this._renderSkills(rubricSkills)}
        </div>
      </div>
    );
  }

  _renderUnsavedChangesWarningDialog() {
    let isSaving = false;
    return (
      <UnsavedChangesWarning
        isOpen={!!this._isNavigationWithChangesRequested()}
        isSaving={this.props.isAnyApiCallSaving || isSaving}
        onSaveClicked={() => {
          isSaving = true;
          this._handleSave().then(() => {
            isSaving = false;
            this._forcePushRequestedLocation();
          });
        }}
        onDiscardClicked={() => {
          this._buildSubSkillsToAbilityIdsMap(this.props);
          this._forcePushRequestedLocation();
        }}
        onCloseClicked={() => {
          this._clearRequestedLocation();
        }}
      />
    );
  }

  _onTargetLanguagePopupOKClick() {
    this.setState({
      targetLanguagePopup: {
        isOpen: false,
      },
    });
  }

  _onTargetLanguageButtonClick(skillId) {
    this.setState({
      targetLanguagePopup: {
        isOpen: true,
        skillId,
      },
    });
  }

  _renderTargetLanguagePopup() {
    const { rubricSkills } = this.props;
    const { targetLanguagePopup } = this.state;
    const currentSkill = rubricSkills.find(
      (skill) => skill.skillId === targetLanguagePopup.skillId,
    );
    return (
      <InfoDialog
        isOpen={targetLanguagePopup.isOpen}
        onOKClick={() => this._onTargetLanguagePopupOKClick()}
      >
        <h3>Target Language</h3>
        {is.existy(currentSkill) &&
          is.existy(currentSkill.targetLanguageCategories) &&
          currentSkill.targetLanguageCategories.map((tlc) => (
            <Detailsboard
              key={tlc.title}
              boardTitle={tlc.title}
              boardValue={tlc.items.join("\n")}
            />
          ))}
      </InfoDialog>
    );
  }

  render() {
    return (
      <div>
        {this._renderView()}
        {this._renderUnsavedChangesWarningDialog()}
        {this._renderTargetLanguagePopup()}
      </div>
    );
  }
}

ProgressTestRubricView.isInMarkingRoute = (props) =>
  props.params && props.params.mode === MARKING;

ProgressTestRubricView.propTypes = Object.assign(
  {},
  UnsavedChangesTrackingPageComponent.propTypes,
  {
    progressTest: PropTypes.object.isRequired,
    currentMember: PropTypes.object.isRequired,
    currentProgressTestId: PropTypes.string.isRequired,
    currentProgressTestIndex: PropTypes.number.isRequired,
    studyingMembers: PropTypes.object.isRequired,
    rubricSkills: PropTypes.objectOf(List),
    submitProgressTestResults: PropTypes.func.isRequired,
    params: PropTypes.object.isRequired,
    route: PropTypes.any.isRequired,
    buildRoute: PropTypes.func,
    pushRoute: PropTypes.func,
    pathName: PropTypes.string,
    progressReportId: PropTypes.string,
    isAnyApiCallSaving: PropTypes.bool,
    isUnitQuiz: PropTypes.bool.isRequired,
  },
);

export function mapStateToProps(state, ownProps) {
  const isUnitQuiz = checkIsUnitQuiz(ownProps.location.pathname);
  const { groupId, studentId } = ownProps.params;
  const currentProgressTestIndex = Number.parseInt(
    ownProps.params.progressTestIndex,
    10,
  );
  const currentProgressTestId = getProgressTestPlanFromIndex(
    state,
    groupId,
    currentProgressTestIndex,
    isUnitQuiz,
  ).id;
  const progressTest = getCurrentProgressTest(state, currentProgressTestId);
  const pathName = ownProps.location.pathname;
  const studyingMembers = GroupMembers.getMembersByGroup(state, groupId).filter(
    (member) => member.isInProduction,
  );
  const currentMember = GroupMembers.getMemberById(state, studentId);
  const progressReportId = isUnitQuiz
    ? ProgressTest.actions.getProgressReportId(
        state,
        groupId,
        currentProgressTestId,
      )
    : undefined;

  const rubricSkills =
    ApiResponseHelper.hasSucceeded(progressTest) &&
    progressTest.data.skills.filter((skill) => skill.isRubric);
  return {
    studyingMembers,
    currentMember,
    rubricSkills: rubricSkills || new List(),
    progressTest: progressTest.data,
    currentProgressTestId,
    currentProgressTestIndex,
    progressReportId,
    pathName,
    isAnyApiCallSaving: !!isAnyApiCallSaving(state),
    isUnitQuiz,
  };
}

function mapDispatchToProps(dispatch) {
  return Object.assign(
    { pushRoute: (path) => dispatch(push(path)) },
    bindActionCreators(ProgressTest.actions, dispatch),
  );
}

function isAnyApiCallSaving(state) {
  return !!(state.get("apiCalls") || new List())
    .filter((call, key) =>
      ["PUT", "POST"].some((method) => key.startsWith(method)),
    )
    .find((call) => ApiCall.State.isLoading(call));
}

function getCurrentProgressTest(state, progressTestId) {
  const progressTests = state.get("rootReducer").get("progressTests");
  if (!Map.isMap(progressTests)) {
    return;
  }
  return progressTests.get(progressTestId);
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(ProgressTestRubricView));



// WEBPACK FOOTER //
// ./containers/Group/MidtermAndFinalExamHFv4/RubricView/component.jsx